import { createFileRoute } from "@tanstack/react-router";
import { DocPage } from "@/components/DocPage";

export const Route = createFileRoute("/trust")({
  head: () => ({
    meta: [
      { title: "Trust & Security — Want To Buy" },
      {
        name: "description",
        content:
          "How Want To Buy protects your account and data — authentication, data handling, and how to report a security issue. Buyer and seller protection for transactions comes from PayPal Goods & Services, not from Want To Buy.",
      },
      { property: "og:title", content: "Trust & Security — Want To Buy" },
      {
        property: "og:description",
        content:
          "Authentication, data handling, and security contacts for the Want To Buy connection platform.",
      },
    ],
  }),
  component: TrustPage,
});

function TrustPage() {
  return (
    <DocPage
      eyebrow="Trust & Security"
      title="How we protect your account and data"
      intro="Want To Buy is a connection platform. This page describes how we protect your account and personal data. Buyer and seller protection for individual transactions is provided by your payment provider, not by Want To Buy."
    >
      <section className="panel-note !mt-0">
        <p className="!mt-0">
          <strong>What Want To Buy is not responsible for.</strong> Want To Buy is a connection platform — it does not process payments, hold funds, verify card authenticity, guarantee condition, or mediate disputes. Buyer and seller protection for transactions comes from PayPal Goods &amp; Services, which is strongly recommended for every transaction.
        </p>
      </section>

      <section>
        <h2>Account authentication</h2>
        <ul>
          <li>Email + password sign-in with strength requirements enforced on the client and server.</li>
          <li>Optional two-factor authentication (authenticator app or SMS).</li>
          <li>Internal operations tooling is restricted to staff accounts, requires an additional role check on every request, and enforces a short inactivity window.</li>
          <li>Passwords are never stored by Want To Buy directly — credential storage and verification are handled by our authentication provider.</li>
        </ul>
      </section>

      <section>
        <h2>Data access controls</h2>
        <ul>
          <li>Database row-level security policies scope each query to the signed-in user, so accounts can only read and modify their own records by default.</li>
          <li>Privileged actions run server-side and re-verify the caller's role before touching shared data.</li>
          <li>Every administrative action against user data is recorded in an append-only audit log.</li>
        </ul>
      </section>

      <section>
        <h2>Data we collect</h2>
        <ul>
          <li>Account profile: username, email, country, and optional avatar.</li>
          <li>Platform activity: buy orders you post, offers you submit, mandatory timestamped photos, and messages with counterparties.</li>
          <li>Standard server logs (IP address, user agent, timestamps) used to investigate abuse.</li>
        </ul>
      </section>

      <section>
        <h2>Retention and deletion</h2>
        <p>
          Account data is retained while your account is active. You can request deletion of your account from the Account settings page. Some records (audit logs, banned-account records) may be retained for the period required to prevent ban evasion and satisfy legal obligations.
        </p>
      </section>

      <section>
        <h2>Reporting a security issue</h2>
        <p>
          If you believe you have found a vulnerability, please email{" "}
          <a href="mailto:security@wanttobuy.ca">security@wanttobuy.ca</a> with a description and steps to reproduce. Please do not publicly disclose the issue until we have had a reasonable chance to investigate and fix it.
        </p>
      </section>

      <section>
        <h2>Changes to this page</h2>
        <p>
          We update this page as our practices change. It describes the controls in place today and should not be read as a guarantee of any particular regulatory certification.
        </p>
      </section>
    </DocPage>
  );
}
